import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import LanguageSelector from '../components/LanguageSelector';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { searchUserByEmail, login } from '../services/userApi';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setUserSession, isAppLaunched } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || '/home';

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (e.target.name === 'email') {
      setEmailExists(null);
      setError('');
    }
  };

  const handleEmailBlur = async () => {
    setError('');
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setEmailExists(null);
      return;
    }

    setIsCheckingEmail(true);
    try {
      const existingUsers = await searchUserByEmail(formData.email);
      if (existingUsers && existingUsers.length > 0) {
        setEmailExists(true);
      } else {
        setEmailExists(false);
        setError('No account found with this email. Please create a new account.');
      }
    } catch (err) {
      setError('Failed to verify email. Please try again.');
      setEmailExists(null);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (emailExists !== true) {
      setError('Please enter a valid and registered email address.');
      setLoading(false);
      return;
    }

    try {
      // User exists, proceed with login via custom API
      const sessionData = await login({
        email: formData.email,
        password: formData.password,
      });

      // After successful login, fetch the full user profile by email
      // to ensure the auth context is populated with all necessary details.
      const users = await searchUserByEmail(formData.email);
      if (!users || users.length === 0) {
        // This should not happen if the email blur validation passed, but as a safeguard:
        throw new Error("Could not retrieve user profile after login.");
      }

      const fullUser = users[0];
      // Create a session object that includes the user details
      // and mimics the structure other pages expect (e.g., with 'sub' and 'attributes').
      const sessionWithUser = {
        ...sessionData, // Contains token from login()
        user: {
          ...fullUser,
          sub: fullUser.userId, // Add 'sub' property for compatibility with ProfilePage
          role: sessionData.role, // Add role to the user object
          attributes: { // Mimic Cognito structure
            name: fullUser.fullName,
            email: fullUser.email,
            phone_number: fullUser.phone,
          },
        },
      };
      setUserSession(sessionWithUser);

      // Redirect based on user role
      if (sessionData.role === 'admin') {
        // Assuming '/admin/dashboard' is the main admin page.
        // You might need to adjust this route based on your routing setup.
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      // The apiUtils now correctly parses the error message.
      if (err.message && err.message.toLowerCase().includes('invalid credentials')) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(err.message || 'An error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isAppLaunched) {
    return (
      <div className="app-container" style={{ background: 'linear-gradient(rgb(11, 58, 122) 0%, rgb(11, 35, 74) 100%)', minHeight: '100vh' }}>
        <div className="login-page-content-wrapper">
          <h1 className="brand-title">{t('common.brand')}</h1>
          <p className="brand-tagline">"{t('common.tagline')}"</p>
          <div className="auth-card fade-in" style={{ textAlign: 'center' }}>
            <h2 style={{ color: 'white', marginBottom: '1rem' }}>Coming Soon!</h2>
            <p style={{ color: 'white', lineHeight: 1.5, marginBottom: '2rem' }}>
              Thank you for your interest. This application will officially launch on
              <br />
              <strong>January 22, 2026</strong>.
            </p>
            <div className="divider"></div>
            <button type="button" className="btn-secondary" onClick={() => navigate('/registration')}>
              {t('auth.createAccount', 'Create New User')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" style={{ background: 'linear-gradient(rgb(11, 58, 122) 0%, rgb(11, 35, 74) 100%)', minHeight: '100vh' }}>
    <div className="login-page-content-wrapper">
        <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
          <LanguageSelector />
        </div>

        <h1 className="brand-title">{t('common.brand')}</h1>
        <p className="brand-tagline">"{t('common.tagline')}"</p>

        <div className="auth-card fade-in">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="email"
                name="email"
                className="form-input"
                placeholder={t('auth.email')}
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                required
              />
            </div>
            {isCheckingEmail && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'white', fontSize: '0.9rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                <Loader2 className="animate-spin" size={18} />
                <span>Checking email...</span>
              </div>
            )}

            <div className="form-group" style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="form-input"
                placeholder={t('auth.password')}
                value={formData.password}
                onChange={handleInputChange}
                disabled={emailExists !== true}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {showPassword ? <EyeOff size={20} color="#333" /> : <Eye size={20} color="#333" />}
              </button>
            </div>

            <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '1rem' }}>
              <Link
                to="/forgot-password"
                style={{  cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
              >
              Forgot Password?
              </Link>
            </div>

            {error && (
              <div style={{ color: '#ff8a80', marginBottom: '1rem', textAlign: 'center' }}>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading || isCheckingEmail || emailExists !== true}
            >
              {loading ? t('common.loading') : t('auth.signIn')}
            </button>
          </form>

          <div className="divider"><span>{t('auth.or', 'or')}</span></div>

          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/registration')}>
            {t('auth.createAccount', 'Create New User')}
          </button>
          {/* <div className="social-login-container">
            <button 
              className="social-btn"
              onClick={() => handleSocialLogin('Google')}
              title="Continue with Google"
            >
              <span style={{ color: '#4285f4', fontWeight: 'bold' }}>G</span>
            </button>
            <button 
              className="social-btn"
              onClick={() => handleSocialLogin('Microsoft')}
              title="Continue with Microsoft"
            >
              <span style={{ color: '#00a1f1', fontWeight: 'bold' }}>M</span>
            </button>
            <button 
              className="social-btn"
              onClick={() => handleSocialLogin('Apple')}
              title="Continue with Apple"
            >
              <span style={{ color: '#000', fontWeight: 'bold' }}>🍎</span>
            </button>
          </div> */}

          {/* <div className="divider">
            <span>{t('auth.or')}</span>
          </div> */}

          {/* <button 
            className="btn-secondary"
            onClick={handleMobileOTPLogin}
          >
            {t('auth.mobileOtpLogin')}
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;